import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  message,
  Modal,
  Dropdown,
  Menu,
  Card,
  Form,
  Input,
  Row,
  Col,
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Status } from '../helpers/consts/status';
import {
  fetchTags,
  addTag,
  removeTag,
  editTag,
} from '../store/actions/tags-actions';
import Spinner from '../components/UI/Spinner/Spinner';
import PageHeader from '../components/UI/PageHeader/PageHeader';

const Tags = () => {
  const { t } = useTranslation();
  const { status, tags } = useSelector((state) => state.tags);
  const dispatch = useDispatch();

  const [openModal, setOpenModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(fetchTags());
    if (status === Status.IDLE && !tags.length) {
      dispatch(fetchTags());
    }
  }, [dispatch]);

  const handleSubmitTag = () => {
    form
      .validateFields()
      .then((values) => {
        const { tagName, position } = values;

        const tagData = { name: tagName };
        if (position) {
          tagData.position = position; // Add position if provided
        }

        if (editingTag) {
          dispatch(editTag({ tagId: editingTag.id, body: tagData }));
        } else {
          dispatch(addTag(tagData));
        }

        form.resetFields();
        setOpenModal(false);
        setEditingTag(null);
      })
      .catch((info) => {
        message.error('Tag name is required!');
      });
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    form.setFieldsValue({ tagName: tag.name, position: tag.position });
    setOpenModal(true);
  };

  const handleDeleteTag = (tagId) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this tag?',
      onOk: () => dispatch(removeTag(tagId)),
    });
  };

  const menuItems = (tag) => [
    {
      label: t('common.edit'),
      key: 'edit',
      icon: <EditOutlined />,
      onClick: () => handleEditTag(tag),
    },
    {
      label: t('common.delete'),
      key: 'delete',
      danger: true,
      icon: <DeleteOutlined />,
      onClick: () => handleDeleteTag(tag.id),
    },
  ];

  let content;
  if (status === Status.LOADING) {
    content = <Spinner />;
  } else if (status === Status.COMPLETED) {
    content = (
      <div className="container-fluid p-0">
        <Card title="Tags" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            {tags.map((tag) => (
              <Col key={tag.id} xs={24} sm={24} md={12} lg={6} xl={6} xxl={4}>
                <Card style={{ height: '100%' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div>
                      <h3>
                        {tag.name}
                        {tag.position && (
                          <span
                            style={{ fontWeight: '400', marginLeft: '10px' }}
                          >
                            ({tag?.position})
                          </span>
                        )}
                      </h3>
                    </div>

                    <Dropdown.Button
                      style={{ zIndex: 1 }}
                      overlay={<Menu items={menuItems(tag)} />}
                    />
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        onAdd={() => {
          setOpenModal(true);
          form.resetFields();
        }}
        showButton
        buttonText={t('common.add') + ' Tag'}
      />
      {content}
      <Modal
        title={
          editingTag ? t('common.edit') + ' Tag' : t('common.add') + ' New Tag'
        }
        open={openModal}
        onCancel={() => {
          setOpenModal(false);
          setEditingTag(null);
        }}
        onOk={handleSubmitTag}
        okText={t('common.ok')}
        cancelText={t('common.cancel')}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="tagName"
            label={t('common.name')}
            rules={[{ required: true, message: t('form.validationMessages.requiredField', { field: 'Tag' }) }]}
          >
            <Input placeholder="Enter tag name" />
          </Form.Item>
          <Form.Item name="position" label="Position">
            <Input placeholder="Position" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Tags;
